import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";

// Shared primitives for the iOS-mirrored onboarding flow (NewOnboardingKit
// port). Every step composes these instead of redeclaring the same inline
// styles.

export const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

// Alpha-video source picker: Safari plays HEVC-alpha .mov natively but not
// VP9-alpha webm; Chrome/Firefox are the reverse — and Mac Chrome CLAIMS
// mp4/hvc1 support then chokes on the alpha layer, so source-order fallback
// is not enough. Pick one source per browser instead.
const IS_SAFARI = /^((?!chrome|android).)*safari/i.test(
  typeof navigator !== "undefined" ? navigator.userAgent : ""
);
export const ClipSource = ({ clip }) =>
  IS_SAFARI ? (
    <source src={`/assets/panda-clips/${clip}.mov`} type='video/mp4; codecs="hvc1"' />
  ) : (
    <source src={`/assets/panda-clips/${clip}.webm`} type="video/webm" />
  );

export const NO = {
  accent: "#0065F8",
  ink: "#0B1A33",
  subtitle: "#5A6B85",
  cardStroke: "#E8EDF5",
  track: "#DDE6F2",
  red: "#FF4B4B",
  deepBlue: "#073B8E",
};

// Eyebrow + title (with one accent-colored segment) + optional subtitle.
export const StepHeading = ({ eyebrow, title, accent, subtitle }) => (
  <div className="w-full text-center sm:text-left">
    {eyebrow && (
      <div
        style={{
          ...FREDOKA,
          color: NO.accent,
          fontWeight: 700,
          fontSize: 14,
          letterSpacing: 2,
          textTransform: "uppercase",
        }}
      >
        {eyebrow}
      </div>
    )}
    <h1
      style={{
        ...FREDOKA,
        color: NO.ink,
        fontWeight: 700,
        fontSize: "clamp(28px, 5vw, 34px)",
        lineHeight: 1.15,
        marginTop: eyebrow ? 8 : 0,
      }}
    >
      {title}
      {accent && <span style={{ color: NO.accent }}> {accent}</span>}
    </h1>
    {subtitle && (
      <p style={{ ...FREDOKA, color: NO.subtitle, fontSize: 17, marginTop: 10 }}>
        {subtitle}
      </p>
    )}
  </div>
);

// Small floating mascot used at the top of most steps. Pass `clip` to play
// one of the transparent Pip animations (converted from the panda_vids
// Supabase bucket: HEVC-alpha .mov for Safari, VP9-alpha .webm for
// Chrome/Firefox); falls back to the static image if video fails.
export const Mascot = ({ size = 110, clip }) => {
  const [videoFailed, setVideoFailed] = useState(false);
  const shadow = { filter: "drop-shadow(0px 6px 12px rgba(11,26,51,0.18))" };
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,101,248,0.14)",
          filter: "blur(24px)",
          borderRadius: "50%",
          transform: "scale(1.3)",
        }}
      />
      {clip && !videoFailed ? (
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          onError={() => setVideoFailed(true)}
          className="relative w-full h-full object-contain z-10 pointer-events-none"
          style={shadow}
        >
          <ClipSource clip={clip} />
        </video>
      ) : (
        <img
          src="/assets/pixley-panda-nobg.png"
          alt=""
          className="relative w-full h-full object-contain z-10"
          onError={(e) => {
            e.target.src = "/assets/pixley-panda.png";
          }}
          style={shadow}
        />
      )}
    </div>
  );
};

// Selectable row card (radio-style), matching iOS NOSelectableRow.
export const SelectableRow = ({ icon, iconBg, title, subtitle, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left transition active:scale-[0.98]"
    style={{
      background: "#FFFFFF",
      borderRadius: 16,
      border: selected ? `2px solid ${NO.accent}` : `1px solid ${NO.cardStroke}`,
      boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
      padding: 14,
      display: "flex",
      alignItems: "center",
      gap: 12,
      cursor: "pointer",
    }}
  >
    <span
      className="flex items-center justify-center flex-shrink-0"
      style={{
        width: 46,
        height: 46,
        borderRadius: 12,
        background: iconBg || "#E3F0FF",
        fontSize: 22,
      }}
    >
      {icon}
    </span>
    <span className="flex-1 min-w-0">
      <span style={{ ...FREDOKA, display: "block", color: NO.ink, fontWeight: 600, fontSize: 17 }}>
        {title}
      </span>
      {subtitle && (
        <span style={{ ...FREDOKA, display: "block", color: NO.subtitle, fontSize: 14 }}>
          {subtitle}
        </span>
      )}
    </span>
    <span
      className="flex-shrink-0 flex items-center justify-center"
      style={{
        width: 24,
        height: 24,
        borderRadius: "50%",
        border: selected ? `7px solid ${NO.accent}` : `2px solid ${NO.track}`,
        background: "#FFFFFF",
        transition: "border 0.15s ease",
      }}
    />
  </button>
);

// Portals the step's Continue button into the shell's footer slot.
export const ContinueSlot = ({ children }) => {
  const [target, setTarget] = useState(null);
  useEffect(() => {
    const timer = setTimeout(() => {
      const el = document.getElementById("onboarding-continue-slot");
      if (el) setTarget(el);
    }, 50);
    return () => clearTimeout(timer);
  }, []);
  if (!target) return null;
  return ReactDOM.createPortal(children, target);
};

// Standard footer Continue pill (web brand 3D-press style, iOS chevron).
export const ContinueButton = ({ onClick, disabled, loading, label = "Continue" }) => (
  <button
    onClick={onClick}
    disabled={disabled || loading}
    className="inline-flex items-center gap-2 transition active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
    style={{
      ...FREDOKA,
      background: NO.accent,
      color: "#FFFFFF",
      fontWeight: 700,
      fontSize: 15,
      border: "none",
      borderRadius: 999,
      padding: "12px 22px",
      boxShadow: `0px 4px 0px ${NO.deepBlue}, 0px 12px 24px rgba(0,101,248,0.22)`,
      cursor: disabled || loading ? "not-allowed" : "pointer",
    }}
  >
    {loading ? (
      <>
        <span
          style={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            border: "2px solid rgba(255,255,255,0.30)",
            borderTopColor: "#FFFFFF",
            animation: "ob-spin 1s linear infinite",
          }}
        />
        Loading…
      </>
    ) : (
      <>
        {label}
        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </>
    )}
  </button>
);
